<template>
    <section v-if="clarifications.length" class="pa-card rma-clar">
        <header class="rma-clar__header">
            <div>
                <h2 class="rma-clar__title">
                    <v-icon small color="primary">mdi-comment-question-outline</v-icon>
                    Pedidos de esclarecimento
                    <span v-if="open.length" class="rma-clar__count">{{ open.length }} aguardando resposta</span>
                </h2>
                <p class="rma-clar__subtitle">A Administração Judicial pediu as informações abaixo sobre {{ competenceLabel }}. A resposta fica registrada.</p>
            </div>
        </header>

        <article v-for="item in open" :key="item.id" class="rma-clar__item rma-clar__item--open">
            <div class="rma-clar__meta">
                <span class="rma-clar__about">{{ aboutText(item) }}</span>
                <span>· pedido em {{ formatDateTime(item.asked_at) }}</span>
            </div>
            <div class="rma-clar__question">{{ item.question }}</div>
            <v-textarea v-model="answers[item.id]" outlined dense rows="3" auto-grow hide-details="auto" maxlength="4000" counter="4000"
                placeholder="Escreva aqui a sua resposta" class="mt-3" :disabled="saving === item.id"></v-textarea>
            <div class="d-flex justify-end mt-2">
                <v-btn color="primary" depressed :loading="saving === item.id" :disabled="!canSend(item)" @click="send(item)">
                    <v-icon left small>mdi-send-outline</v-icon>Enviar resposta
                </v-btn>
            </div>
        </article>

        <template v-if="done.length">
            <button type="button" class="rma-clar__toggle" @click="showDone = !showDone">
                <v-icon small>{{ showDone ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
                {{ showDone ? "Ocultar" : "Ver" }} pedidos respondidos ({{ done.length }})
            </button>
            <template v-if="showDone">
                <article v-for="item in done" :key="item.id" class="rma-clar__item">
                    <div class="rma-clar__meta">
                        <span class="rma-clar__about">{{ aboutText(item) }}</span>
                        <span>· pedido em {{ formatDateTime(item.asked_at) }}</span>
                    </div>
                    <div class="rma-clar__question">{{ item.question }}</div>
                    <div v-if="item.answer" class="rma-clar__answer">
                        <div class="rma-clar__answer-meta">Resposta de {{ item.answered_by_name || "recuperanda" }} em {{ formatDateTime(item.answered_at) }}</div>
                        {{ item.answer }}
                    </div>
                    <div v-else class="rma-clar__answer-meta">Encerrado pela Administração Judicial sem resposta.</div>
                </article>
            </template>
        </template>
    </section>
</template>

<script>
import axios from "axios";
import { formatDateTime, notifyError, notifySuccess } from "@/components/rma/rma.util";

// Pedidos de esclarecimento da Administracao Judicial na competencia: a devedora
// responde aqui (gerais e sobre documentos especificos).
export default {
    name: "RmaClarifications",

    props: {
        clarifications: { type: Array, default: () => [] },
        competenceLabel: { type: String, default: "" },
    },

    data: () => ({ answers: {}, saving: null, showDone: false }),

    computed: {
        open() {
            return this.clarifications.filter((item) => item.status === "open");
        },
        done() {
            return this.clarifications.filter((item) => item.status !== "open");
        },
    },

    methods: {
        formatDateTime,

        aboutText(item) {
            if (!item.document_name) return "Pergunta geral";
            return `Sobre: ${item.document_name}${item.company_name ? ` · ${item.company_name}` : ""}`;
        },

        canSend(item) {
            return String(this.answers[item.id] || "").trim().length >= 2;
        },

        async send(item) {
            this.saving = item.id;
            try {
                const result = await axios.post(`/api/client/rma/clarifications/${item.id}/answer`, { answer: String(this.answers[item.id] || "").trim() });
                this.$delete(this.answers, item.id);
                this.$emit("answered", result.data.content);
                notifySuccess(this, "Resposta enviada à Administração Judicial.");
            } catch (error) {
                notifyError(this, error, "Não foi possível enviar a resposta.");
            } finally {
                this.saving = null;
            }
        },
    },
};
</script>

<style scoped lang="scss">
.rma-clar {
    margin-bottom: 16px;
    padding: 18px 22px 10px;
    border-left: 3px solid #2b2050;
}

.rma-clar__item:last-child {
    padding-bottom: 8px;
}

.rma-clar__title {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    font-size: 16px;
    font-weight: 600;
    margin: 0;
}

.rma-clar__count {
    font-size: 12px;
    font-weight: 600;
    padding: 2px 10px;
    border-radius: 999px;
    background: #fff4e5;
    color: #8a5a00;
}

.rma-clar__subtitle {
    font-size: 13px;
    color: #6b6880;
    margin: 4px 0 8px;
}

.rma-clar__item {
    padding: 14px 0;
    border-top: 1px solid #eef0f3;
}

.rma-clar__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    font-size: 12.5px;
    color: #6b6880;
}

.rma-clar__about {
    font-weight: 600;
    color: #2b2050;
}

.rma-clar__question {
    margin-top: 6px;
    padding: 10px 12px;
    border-radius: 8px;
    background: #f6f4fb;
    white-space: pre-line;
}

.rma-clar__answer {
    margin-top: 8px;
    padding: 10px 12px;
    border-radius: 8px;
    background: #f0fdf4;
    white-space: pre-line;
}

.rma-clar__answer-meta {
    font-size: 12px;
    color: #6b6880;
    margin-top: 6px;
}

.rma-clar__answer .rma-clar__answer-meta {
    margin: 0 0 4px;
}

.rma-clar__toggle {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-top: 8px;
    padding: 6px 0;
    background: none;
    border: 0;
    font: inherit;
    font-size: 13px;
    font-weight: 600;
    color: #2b2050;
    cursor: pointer;
}
</style>
